<template>
  <div class="voucher">
    <h2 class="voucher__title title-medium">{{ $t('payment.voucher.title') }}</h2>
    <div class="voucher__action">
      <Input
        id="voucher-code"
        :label="$t('payment.voucher.code')"
        :placeholder="$t('payment.voucher.code')"
        v-model="localVoucherCode"
        :internalLabel="true"
        class="voucher__input"
        :disabled="isLoading"
      />
      <button
        class="voucher__add-button"
        @click="handleAddVoucher"
        :disabled="!localVoucherCode || isLoading"
      >
        {{ $t('payment.voucher.add') }}
      </button>
    </div>
    <div v-if="errorMessage" class="voucher__error">
      {{ errorMessage }}
    </div>
    <div class="voucher__tags">
      <div
        v-for="(voucher, index) in vouchers"
        :key="index"
        class="voucher__tag"
      >
        <svg class="voucher__icon">
          <use :xlink:href="`#${discount}`" />
        </svg>
        {{ voucher }}
        <button
          class="voucher__remove-button"
          @click="removeVoucher(index)"
          :disabled="isLoading"
        >
          &times;
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, ref } from 'vue'
  import Input from '@/components/BaseComponents/Input.vue'
  import discount from '@/assets/icons/discount.svg'
  import { addVoucher } from '@/services/journeyService'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'

  export default defineComponent({
    name: 'PaymentVoucher',
    components: {
      Input,
    },
    props: {
      voucherCode: {
        type: String,
        required: true,
      },
      vouchers: {
        type: Array as () => string[],
        required: true,
      },
      bookingNumber: {
        type: String,
        required: false,
        default: '',
      },
    },
    emits: ['update:voucherCode', 'addVoucher', 'removeVoucher'],
    setup(props, { emit }) {
      const bookingStore = useBookingStore()
      const myTravelStore = useMyTravelStore()
      const isLoading = ref(false)

      const localVoucherCode = computed({
        get: () => props.voucherCode,
        set: (value) => emit('update:voucherCode', value),
      })

      const errorMessage = ref('');

      const handleAddVoucher = async () => {
        errorMessage.value = '';
        
        if (!localVoucherCode.value) {
          console.warn('[PaymentVoucher] No voucher code entered');
          return;
        }
        
        if (!props.bookingNumber) {
          console.error('[PaymentVoucher] Cannot apply voucher: No booking number available');
          return;
        }
        
        isLoading.value = true;
        try {
          const response = await addVoucher(props.bookingNumber, localVoucherCode.value);
          
          if (response?.data) {
            if (response.data.messages && response.data.messages.length > 0) {
              const warningMessage = response.data.messages.find((m: any) => m.severity === 'warn');
              if (warningMessage) {
                errorMessage.value = warningMessage.content;
                return;
              }
            }
            
            if (!response.data.vouchers || response.data.vouchers.length === 0) {
              let discountFound = false;
              const segments = [
                ...(response.data.outbound_booking_tariff_segments || []),
                ...(response.data.inbound_booking_tariff_segments || [])
              ];
              
              for (const segment of segments) {
                const products = [
                  ...(segment.required_products || []),
                  ...(segment.additional_products || [])
                ];
                
                for (const product of products) {
                  if (product.discounts && product.discounts.length > 0) {
                    discountFound = true;
                    break;
                  }
                }
              }
              
              if (!discountFound) {
                errorMessage.value = 'Rabattkoden kunne ikke brukes for de valgte produktene.';
                return;
              }
            }
            
            bookingStore.setBookingData(response.data);
            myTravelStore.updateDiscountsAndTotal(response.data);
            emit('addVoucher');
          } else {
            errorMessage.value = 'Det oppstod en feil ved bruk av rabattkoden.';
          }
        } catch (error) {
          console.error('[PaymentVoucher] Error adding voucher:', error);
          errorMessage.value = 'Det oppstod en feil ved bruk av rabattkoden.';
        } finally {
          isLoading.value = false;
        }
      }

      const removeVoucher = (index: number) => {
        emit('removeVoucher', index)
      }

      return {
        localVoucherCode,
        discount,
        isLoading,
        errorMessage,
        handleAddVoucher,
        removeVoucher,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .voucher {
    padding: 1.563rem 0;
    @include body-large;
    display: flex;
    flex-direction: column;
    border-top: 1px solid $clr-primary-100;
    border-bottom: 1px solid $clr-primary-100;

    &__title {
      margin: 0 0 1.563rem 0;
    }

    &__action {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    &__input {
      flex-grow: 1;
    }

    &__add-button {
      background: none;
      border: none;
      color: $clr-primary;
      cursor: pointer;
      margin-left: 0.5rem;
      white-space: nowrap;
    }

    &__tags {
      display: flex;
      flex-wrap: wrap;
    }

    &__tag {
      display: flex;
      align-items: center;
      margin-top: 1.563rem;
      padding: 0.5rem 1.125rem;
      background-color: $clr-white;
      border-radius: 9999px;
      border: 1px solid $clr-primary-50;
    }

    &__icon {
      width: 16px;
      height: 16px;
      margin-right: 0.5rem;
      color: $clr-secondary;
    }

    &__remove-button {
      background: none;
      border: none;
      color: $clr-primary;
      cursor: pointer;
      margin-left: 0.5rem;
    }
    
    &__error {
      color: $clr-error;
      margin-top: 0.5rem;
      font-size: 0.875rem;
    }
  }
</style>
